/**
 * Edit Finding Modal Component
 * Form for editing existing audit findings (within 24-hour window)
 */

import {
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Switch,
  Button,
  Stack,
  Group,
} from "@mantine/core";
import { useAppForm } from "../../hooks/useAppForm";
import { notifications } from "@mantine/notifications";
import { rulesToMantineValidate } from "../../utils/validation";
import {
  AUDIT_FINDING_FIELD_RULES,
  AUDIT_FINDING_FIELD_LABELS,
} from "@livingsyncs/types";
import {
  AuditFinding,
  FindingType,
  FindingSeverity,
  FindingStatus,
} from "../../types/audit";
import { useUpdateAuditFinding } from "../../hooks/useAuditAnnotations";
import { DatePickerInput } from "@mantine/dates";
import { useEffect } from "react";
import { toDateOnly } from "../../utils/mantineDate";

interface EditFindingModalProps {
  opened: boolean;
  onClose: () => void;
  finding: AuditFinding;
  onSuccess?: () => void;
}

interface EditFindingFormValues {
  section: string;
  finding_type: FindingType;
  severity: FindingSeverity;
  title: string;
  description: string;
  recommendations: string;
  regulatory_reference: string;
  rating: number | undefined;
  requires_follow_up: boolean;
  follow_up_date: string | undefined;
  status: FindingStatus;
  is_verified: boolean;
}

export function EditFindingModal({
  opened,
  onClose,
  finding,
  onSuccess,
}: EditFindingModalProps) {
  const updateFinding = useUpdateAuditFinding(finding.id);

  // US-082: shared rule map carries required + max-length bounds; rating
  // bounds and enums (finding_type, severity, status) stay bespoke/Select-
  // constrained. Backend-only rules come back as 400 details onSubmitSafe
  // maps inline.
  const form = useAppForm<EditFindingFormValues>(
    {
      initialValues: {
        section: finding.section || "",
        finding_type: finding.finding_type as FindingType,
        severity: finding.severity as FindingSeverity,
        title: finding.title,
        description: finding.description,
        recommendations: finding.recommendations || "",
        regulatory_reference: finding.regulatory_reference || "",
        // Wire value is a numeric string (VarChar(30) column) — coerce
        // before it round-trips into the z.number() schema.
        rating: finding.rating != null ? Number(finding.rating) : undefined,
        requires_follow_up: finding.requires_follow_up || false,
        follow_up_date: toDateOnly(finding.follow_up_date) ?? undefined,
        status: finding.status,
        is_verified: finding.is_verified || false,
      },
      validate: {
        ...rulesToMantineValidate(
          AUDIT_FINDING_FIELD_RULES,
          AUDIT_FINDING_FIELD_LABELS,
        ),
        rating: (value) => {
          // A cleared NumberInput yields "" — any non-number means no rating.
          if (typeof value !== "number") return null;
          if (value < 1 || value > 5) {
            return "Rating must be between 1 and 5";
          }
          return null;
        },
      },
    },
    {
      errorFallback: "Failed to update finding",
      dateOnlyFields: ["follow_up_date"],
    },
  );

  const { setValues } = form;

  // Reset form when finding changes
  useEffect(() => {
    if (finding && opened) {
      setValues({
        section: finding.section || "",
        finding_type: finding.finding_type as FindingType,
        severity: finding.severity as FindingSeverity,
        title: finding.title,
        description: finding.description,
        recommendations: finding.recommendations || "",
        regulatory_reference: finding.regulatory_reference || "",
        // Wire value is a numeric string (VarChar(30) column) — coerce
        // before it round-trips into the z.number() schema.
        rating: finding.rating != null ? Number(finding.rating) : undefined,
        requires_follow_up: finding.requires_follow_up || false,
        follow_up_date: toDateOnly(finding.follow_up_date) ?? undefined,
        status: finding.status,
        is_verified: finding.is_verified || false,
      });
    }
  }, [finding, opened, setValues]);

  const handleSubmit = async (values: typeof form.values) => {
    await updateFinding.mutateAsync({
      ...values,
      rating: typeof values.rating === "number" ? values.rating : undefined,
      session_id: finding.session_id,
      section: values.section || undefined,
      recommendations: values.recommendations || undefined,
      regulatory_reference: values.regulatory_reference || undefined,
    });

    notifications.show({
      title: "Finding Updated",
      message: "Audit finding has been successfully updated",
      color: "green",
    });

    onClose();
    onSuccess?.();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Audit Finding"
      size="lg"
    >
      <form onSubmit={form.onSubmitSafe(handleSubmit)}>
        <Stack gap="md">
          {/* Section */}
          <TextInput
            label="Section"
            placeholder="e.g., demographics, medical_info"
            {...form.getInputProps("section")}
          />

          {/* Finding Type */}
          <Select
            label="Finding Type"
            withAsterisk
            data={[
              { value: "deficiency", label: "Deficiency" },
              { value: "observation", label: "Observation" },
              { value: "commendation", label: "Commendation" },
              { value: "recommendation", label: "Recommendation" },
            ]}
            {...form.getInputProps("finding_type")}
          />

          {/* Severity */}
          <Select
            label="Severity"
            withAsterisk
            data={[
              { value: "critical", label: "Critical" },
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" },
              { value: "low", label: "Low" },
              { value: "informational", label: "Informational" },
            ]}
            {...form.getInputProps("severity")}
          />

          {/* Rating */}
          <NumberInput
            label="Rating (1-5)"
            placeholder="Optional"
            min={1}
            max={5}
            {...form.getInputProps("rating")}
          />

          {/* Title */}
          <TextInput
            label="Title"
            withAsterisk
            placeholder="Brief description of the finding"
            {...form.getInputProps("title")}
          />

          {/* Description */}
          <Textarea
            label="Description"
            withAsterisk
            placeholder="Detailed description of what was observed"
            minRows={4}
            {...form.getInputProps("description")}
          />

          {/* Recommendations */}
          <Textarea
            label="Recommendations"
            placeholder="Suggested corrective actions or improvements"
            minRows={3}
            {...form.getInputProps("recommendations")}
          />

          {/* Regulatory Reference */}
          <TextInput
            label="Regulatory Reference"
            placeholder="e.g., 42 CFR 483.10"
            {...form.getInputProps("regulatory_reference")}
          />

          {/* Follow-up */}
          <Switch
            label="Requires Follow-up"
            {...form.getInputProps("requires_follow_up", { type: "checkbox" })}
          />

          {form.values.requires_follow_up && (
            <DatePickerInput
              label="Follow-up Date"
              placeholder="Select follow-up date"
              minDate={new Date()}
              {...form.getInputProps("follow_up_date")}
            />
          )}

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={updateFinding.isPending}>
              Update Finding
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
